import { User } from '../reducers/authReducer'

export enum AuthActionTypes {
  SIGNUP_REQUEST = 'AUTH/SIGNUP_REQUEST',
  SIGNUP_SUCCESS = 'AUTH/SIGNUP_SUCCESS',
  SIGNUP_FAILURE = 'AUTH/SIGNUP_FAILURE',

  LOGIN_REQUEST = 'AUTH/LOGIN_REQUEST',
  LOGIN_SUCCESS = 'AUTH/LOGIN_SUCCESS',
  LOGIN_FAILURE = 'AUTH/LOGIN_FAILURE',

  VERIFY_EMAIL_REQUEST = 'AUTH/VERIFY_EMAIL_REQUEST',
  VERIFY_EMAIL_SUCCESS = 'AUTH/VERIFY_EMAIL_SUCCESS',
  VERIFY_EMAIL_FAILURE = 'AUTH/VERIFY_EMAIL_FAILURE',

  CHECK_AUTH_REQUEST = 'AUTH/CHECK_AUTH_REQUEST',
  CHECK_AUTH_SUCCESS = 'AUTH/CHECK_AUTH_SUCCESS',
  CHECK_AUTH_FAILURE = 'AUTH/CHECK_AUTH_FAILURE',

  LOGOUT_REQUEST = 'AUTH/LOGOUT_REQUEST',
  LOGOUT_SUCCESS = 'AUTH/LOGOUT_SUCCESS',
  LOGOUT_FAILURE = 'AUTH/LOGOUT_FAILURE',

  CLEAR_ERROR = 'AUTH/CLEAR_ERROR',
  SET_CREDENTIALS = 'AUTH/SET_CREDENTIALS',
  UPDATE_USER = 'AUTH/UPDATE_USER',
}

export interface SignupData {
  firstName: string
  lastName: string
  companyName: string
  contactNumber: string
  email: string
  password: string
  confirmPassword: string
}

export interface LoginCredentials {
  email: string
  password: string
  loginType?: 'user' | 'admin'
}

export interface LoginRequestAction {
  type: AuthActionTypes.LOGIN_REQUEST
  payload: LoginCredentials
  [key: string]: any
}

export interface LoginSuccessAction {
  type: AuthActionTypes.LOGIN_SUCCESS
  payload: {
    user: User
    token: string
  }
  [key: string]: any
}

export interface LoginFailureAction {
  type: AuthActionTypes.LOGIN_FAILURE
  payload: string
  [key: string]: any
}

export interface CheckAuthRequestAction {
  type: AuthActionTypes.CHECK_AUTH_REQUEST
  [key: string]: any
}

export interface CheckAuthSuccessAction {
  type: AuthActionTypes.CHECK_AUTH_SUCCESS
  payload: {
    user: User
  }
  [key: string]: any
}

export interface CheckAuthFailureAction {
  type: AuthActionTypes.CHECK_AUTH_FAILURE
  payload: string
  [key: string]: any
}

export interface LogoutRequestAction {
  type: AuthActionTypes.LOGOUT_REQUEST
  [key: string]: any
}

export interface LogoutSuccessAction {
  type: AuthActionTypes.LOGOUT_SUCCESS
  [key: string]: any
}

export interface LogoutFailureAction {
  type: AuthActionTypes.LOGOUT_FAILURE
  payload: string
  [key: string]: any
}

export interface ClearErrorAction {
  type: AuthActionTypes.CLEAR_ERROR
  [key: string]: any
}

export interface SetCredentialsAction {
  type: AuthActionTypes.SET_CREDENTIALS
  payload: {
    user: User
    token: string
  }
  [key: string]: any
}

export interface SignupRequestAction {
  type: AuthActionTypes.SIGNUP_REQUEST
  payload: SignupData
  [key: string]: any
}

export interface SignupSuccessAction {
  type: AuthActionTypes.SIGNUP_SUCCESS
  [key: string]: any
}

export interface SignupFailureAction {
  type: AuthActionTypes.SIGNUP_FAILURE
  payload: string
  [key: string]: any
}

export interface VerifyEmailRequestAction {
  type: AuthActionTypes.VERIFY_EMAIL_REQUEST
  payload: string
  [key: string]: any
}

export interface VerifyEmailSuccessAction {
  type: AuthActionTypes.VERIFY_EMAIL_SUCCESS
  payload: {
    user: User
    token: string
  }
  [key: string]: any
}

export interface VerifyEmailFailureAction {
  type: AuthActionTypes.VERIFY_EMAIL_FAILURE
  payload: string
  [key: string]: any
}

export interface UpdateUserAction {
  type: AuthActionTypes.UPDATE_USER
  payload: {
    user: User
  }
  [key: string]: any
}

export type AuthAction =
  | SignupRequestAction
  | SignupSuccessAction
  | SignupFailureAction
  | VerifyEmailRequestAction
  | VerifyEmailSuccessAction
  | VerifyEmailFailureAction
  | LoginRequestAction
  | LoginSuccessAction
  | LoginFailureAction
  | CheckAuthRequestAction
  | CheckAuthSuccessAction
  | CheckAuthFailureAction
  | LogoutRequestAction
  | LogoutSuccessAction
  | LogoutFailureAction
  | ClearErrorAction
  | SetCredentialsAction
  | UpdateUserAction

// Signup action creators
export const signupRequest = (data: SignupData): SignupRequestAction => ({
  type: AuthActionTypes.SIGNUP_REQUEST,
  payload: data,
})

export const signupSuccess = (): SignupSuccessAction => ({
  type: AuthActionTypes.SIGNUP_SUCCESS,
})

export const signupFailure = (error: string): SignupFailureAction => ({
  type: AuthActionTypes.SIGNUP_FAILURE,
  payload: error,
})

// Email verification action creators
export const verifyEmailRequest = (token: string): VerifyEmailRequestAction => ({
  type: AuthActionTypes.VERIFY_EMAIL_REQUEST,
  payload: token,
})

export const verifyEmailSuccess = (user: User, token: string): VerifyEmailSuccessAction => ({
  type: AuthActionTypes.VERIFY_EMAIL_SUCCESS,
  payload: { user, token },
})

export const verifyEmailFailure = (error: string): VerifyEmailFailureAction => ({
  type: AuthActionTypes.VERIFY_EMAIL_FAILURE,
  payload: error,
})

// Login action creators
export const loginRequest = (credentials: LoginCredentials): LoginRequestAction => ({
  type: AuthActionTypes.LOGIN_REQUEST,
  payload: credentials,
})

export const loginSuccess = (user: User, token: string): LoginSuccessAction => ({
  type: AuthActionTypes.LOGIN_SUCCESS,
  payload: { user, token },
})

export const loginFailure = (error: string): LoginFailureAction => ({
  type: AuthActionTypes.LOGIN_FAILURE,
  payload: error,
})

export const checkAuthRequest = (): CheckAuthRequestAction => ({
  type: AuthActionTypes.CHECK_AUTH_REQUEST,
})

export const checkAuthSuccess = (user: User): CheckAuthSuccessAction => ({
  type: AuthActionTypes.CHECK_AUTH_SUCCESS,
  payload: { user },
})

export const checkAuthFailure = (error: string): CheckAuthFailureAction => ({
  type: AuthActionTypes.CHECK_AUTH_FAILURE,
  payload: error,
})

export const logoutRequest = (): LogoutRequestAction => ({
  type: AuthActionTypes.LOGOUT_REQUEST,
})

export const logoutSuccess = (): LogoutSuccessAction => ({
  type: AuthActionTypes.LOGOUT_SUCCESS,
})

export const logoutFailure = (error: string): LogoutFailureAction => ({
  type: AuthActionTypes.LOGOUT_FAILURE,
  payload: error,
})

export const clearError = (): ClearErrorAction => ({
  type: AuthActionTypes.CLEAR_ERROR,
})

export const setCredentials = (user: User, token: string): SetCredentialsAction => ({
  type: AuthActionTypes.SET_CREDENTIALS,
  payload: { user, token },
})

export const updateUser = (user: User): UpdateUserAction => ({
  type: AuthActionTypes.UPDATE_USER,
  payload: { user },
})